export const SUPPORTED_LANGUAGES = ["ar", "en"] as const;

export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: AppLanguage = "ar";
export const FALLBACK_LANGUAGE: AppLanguage = "en";
export const LANGUAGE_STORAGE_KEY = "th_admin_language";
export const LANGUAGE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function readLocaleFromCookie(
  cookieValue: string | null | undefined,
): AppLanguage | null {
  if (!cookieValue) return null;
  const normalized = normalizeLanguage(cookieValue);
  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : null;
}

export function normalizeLanguage(value: string | null | undefined): AppLanguage {
  if (!value) return DEFAULT_LANGUAGE;

  const base = value.toLowerCase().split("-")[0];
  return SUPPORTED_LANGUAGES.includes(base as AppLanguage)
    ? (base as AppLanguage)
    : DEFAULT_LANGUAGE;
}

/** Reads the user-selected language from localStorage (client only). */
export function readStoredLanguage(): AppLanguage | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (!stored) return null;

    const normalized = normalizeLanguage(stored);
    return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : null;
  } catch {
    return null;
  }
}

function readLanguageFromDocumentCookie(): AppLanguage | null {
  if (typeof document === "undefined") return null;

  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${LANGUAGE_STORAGE_KEY}=([^;]*)`),
    );
    return readLocaleFromCookie(
      match ? decodeURIComponent(match[1]) : null,
    );
  } catch {
    return null;
  }
}

/** Cookie first, then localStorage (client only). */
export function readClientLanguage(): AppLanguage | null {
  return readLanguageFromDocumentCookie() ?? readStoredLanguage();
}

export function isRtlLanguage(language: string | null | undefined): boolean {
  return normalizeLanguage(language) === "ar";
}

/** Persists the user-selected language to localStorage and a cookie (client only). */
export function persistLanguage(language: AppLanguage): void {
  if (typeof document === "undefined") return;

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // localStorage may be unavailable in private browsing.
  }

  document.cookie = `${LANGUAGE_STORAGE_KEY}=${language};path=/;max-age=${LANGUAGE_COOKIE_MAX_AGE};SameSite=Lax`;
}
